import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChatNotification } from '../../contexts/ChatNotificationContext';
import {
  fetchChatCanais,
  fetchChatMensagens,
  enviarChatMensagem,
  marcarChatComoLida,
} from '../../services/api';
import styles from './ChatFlutuante.module.css';

// ── helpers ──────────────────────────────────────────────────────────────
const AVATAR_CORES = [
  'linear-gradient(135deg, #6366f1, #8b5cf6)',
  'linear-gradient(135deg, #f43f5e, #ec4899)',
  'linear-gradient(135deg, #06b6d4, #3b82f6)',
  'linear-gradient(135deg, #10b981, #059669)',
  'linear-gradient(135deg, #f59e0b, #d97706)',
  'linear-gradient(135deg, #8b5cf6, #d946ef)',
  'linear-gradient(135deg, #14b8a6, #06b6d4)',
  'linear-gradient(135deg, #ef4444, #f97316)',
];
const getAvatarCor = (id) => AVATAR_CORES[(id || 0) % AVATAR_CORES.length];
const getIniciais = (nome) => {
  if (!nome) return '?';
  const p = nome.trim().split(' ');
  return p.length >= 2
    ? (p[0][0] + p[p.length - 1][0]).toUpperCase()
    : nome.substring(0, 2).toUpperCase();
};

const parseDataUTC = (data) => {
  if (!data) return null;
  let str = String(data).trim();
  if (!str.endsWith('Z') && !str.includes('+') && !str.includes('T'))
    str = str.replace(' ', 'T') + 'Z';
  else if (!str.endsWith('Z') && str.includes('T') && !str.includes('+'))
    str += 'Z';
  return new Date(str);
};

const formatarHora = (data) => {
  if (!data) return '';
  const d = parseDataUTC(data);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

// Comprime a imagem (redimensiona p/ máx 1024px) e devolve um data URL JPEG.
const comprimirImagem = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      const MAX = 1024;
      let w = img.width, h = img.height;
      if (w > MAX || h > MAX) {
        if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
        else { w = Math.round(w * MAX / h); h = MAX; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = reject;
    img.src = e.target.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// ── Componente Principal ──────────────────────────────────────────────────
const ChatFlutuante = () => {
  const { user } = useAuth();
  const { socketRef, totalNaoLidas, atualizarNaoLidas, ultimaMensagem } =
    useChatNotification();
  const navigate = useNavigate();

  // ── estado da janela flutuante ────────────────────────────────────────
  const [aberto, setAberto] = useState(false);
  const [minimizado, setMinimizado] = useState(false);
  const [canais, setCanais] = useState([]);
  const [canalAtivo, setCanalAtivo] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  const [imagensPendentes, setImagensPendentes] = useState([]); // data URLs das imagens coladas/anexadas
  const [carregando, setCarregando] = useState(false);
  const [digitandoUsuarios, setDigitandoUsuarios] = useState({});
  const [aba, setAba] = useState('canais'); // 'canais' | 'chat'

  // ── notificação toast ─────────────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef({});

  const mensagensRef = useRef(null);
  const inputRef = useRef(null);
  const digitandoTimerRef = useRef(null);

  // ── carregar canais ───────────────────────────────────────────────────
  const carregarCanais = useCallback(async () => {
    const result = await fetchChatCanais();
    if (result.success) setCanais(result.data);
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    carregarCanais();
  }, [user?.id, carregarCanais]);

  // ── nova mensagem via socket → toast ──────────────────────────────────
  useEffect(() => {
    if (!ultimaMensagem) return;
    if (ultimaMensagem.usuario_id === user?.id) return;
    // Não mostrar toast se o mini-chat estiver aberto no mesmo canal
    if (aberto && !minimizado && canalAtivo?.id === ultimaMensagem.canal_id) return;

    const id = Date.now();
    setToasts((prev) => [...prev.slice(-2), { id, msg: ultimaMensagem }]);

    toastTimers.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      delete toastTimers.current[id];
    }, 6000);

    carregarCanais();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ultimaMensagem]);

  const descartarToast = (id) => {
    clearTimeout(toastTimers.current[id]);
    delete toastTimers.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // ── listeners de socket para o mini-chat ─────────────────────────────
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !user) return;

    const onNovaMensagem = (mensagem) => {
      if (canalAtivo && mensagem.canal_id === canalAtivo.id) {
        setMensagens((prev) => {
          if (prev.find((m) => m.id === mensagem.id)) return prev;
          return [...prev, mensagem];
        });
      }
      carregarCanais();
    };

    const onUsuarioDigitando = ({ canalId, usuario_id, nome }) => {
      setDigitandoUsuarios((prev) => ({
        ...prev,
        [`${canalId}_${usuario_id}`]: { nome, timestamp: Date.now() },
      }));
    };

    const onUsuarioParouDigitar = ({ canalId, usuario_id }) => {
      setDigitandoUsuarios((prev) => {
        const novo = { ...prev };
        delete novo[`${canalId}_${usuario_id}`];
        return novo;
      });
    };

    socket.on('nova-mensagem', onNovaMensagem);
    socket.on('usuario-digitando', onUsuarioDigitando);
    socket.on('usuario-parou-digitar', onUsuarioParouDigitar);

    return () => {
      socket.off('nova-mensagem', onNovaMensagem);
      socket.off('usuario-digitando', onUsuarioDigitando);
      socket.off('usuario-parou-digitar', onUsuarioParouDigitar);
    };
  }, [socketRef, user, canalAtivo, carregarCanais]);

  // ── scroll automático ─────────────────────────────────────────────────
  useEffect(() => {
    if (mensagensRef.current && !minimizado) {
      mensagensRef.current.scrollTop = mensagensRef.current.scrollHeight;
    }
  }, [mensagens, minimizado]);

  // ── selecionar canal ──────────────────────────────────────────────────
  const selecionarCanal = useCallback(
    async (canal) => {
      setCanalAtivo(canal);
      setMensagens([]);
      setInputMsg('');
      setImagensPendentes([]);
      setAba('chat');
      setMinimizado(false);
      setCarregando(true);
      const result = await fetchChatMensagens(canal.id, {});
      if (result.success) setMensagens(result.data);
      setCarregando(false);

      if (socketRef.current) {
        socketRef.current.emit('join-canal', { canalId: canal.id });
      }
      if (canal.nao_lidas > 0 && canal.ultima_msg_id) {
        await marcarChatComoLida(canal.id, canal.ultima_msg_id);
        setCanais((prev) =>
          prev.map((c) => (c.id === canal.id ? { ...c, nao_lidas: 0 } : c))
        );
        atualizarNaoLidas();
      }
    },
    [socketRef, atualizarNaoLidas]
  );

  // ── abrir canal via toast ─────────────────────────────────────────────
  const abrirCanalDoToast = useCallback(
    async (toast) => {
      descartarToast(toast.id);
      setAberto(true);
      setMinimizado(false);
      const canalEncontrado = canais.find((c) => c.id === toast.msg.canal_id);
      if (canalEncontrado) {
        await selecionarCanal(canalEncontrado);
      } else {
        await carregarCanais();
        setAba('canais');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canais, selecionarCanal, carregarCanais]
  );

  // ── colar imagem (Ctrl+V) — aceita várias de uma vez ──────────────────
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const arquivos = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) arquivos.push(file);
      }
    }
    if (arquivos.length === 0) return;
    e.preventDefault();
    try {
      const dataUrls = await Promise.all(arquivos.map((f) => comprimirImagem(f)));
      setImagensPendentes((prev) => [...prev, ...dataUrls]);
    } catch {
      alert('Erro ao processar imagem colada.');
    }
  };

  const removerImagemPendente = (idx) => {
    setImagensPendentes((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── enviar mensagem ───────────────────────────────────────────────────
  const handleEnviar = async () => {
    const texto = inputMsg.trim();
    if ((!texto && imagensPendentes.length === 0) || !canalAtivo) return;

    const imagens = imagensPendentes;
    // Sem imagens: envia só o texto. Com imagens: cada imagem vira uma mensagem
    // (o backend guarda 1 imagem por mensagem); o texto acompanha a primeira.
    const envios = imagens.length === 0
      ? [{ conteudo: texto, imagem: null }]
      : imagens.map((img, i) => ({ conteudo: i === 0 ? texto : '', imagem: img }));

    setInputMsg('');
    setImagensPendentes([]);

    for (const payload of envios) {
      const result = await enviarChatMensagem(canalAtivo.id, payload);
      if (result.success) {
        setMensagens((prev) => {
          if (prev.find((m) => m.id === result.data.id)) return prev;
          return [...prev, result.data];
        });
      }
    }
    carregarCanais();
    inputRef.current?.focus();
    if (socketRef.current && canalAtivo) {
      socketRef.current.emit('parou-digitar', {
        canalId: canalAtivo.id,
        usuario: { id: user.id },
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  const handleInputChange = (e) => {
    setInputMsg(e.target.value);
    if (socketRef.current && canalAtivo) {
      socketRef.current.emit('digitando', {
        canalId: canalAtivo.id,
        usuario: { id: user.id, nome: user.nome },
      });
      if (digitandoTimerRef.current) clearTimeout(digitandoTimerRef.current);
      digitandoTimerRef.current = setTimeout(() => {
        socketRef.current?.emit('parou-digitar', {
          canalId: canalAtivo.id,
          usuario: { id: user.id },
        });
      }, 2000);
    }
  };

  const toggleAberto = () => {
    if (!aberto) {
      setAberto(true);
      setMinimizado(false);
    } else if (!minimizado) {
      setMinimizado(true);
    } else {
      setMinimizado(false);
    }
  };

  const fechar = () => {
    setAberto(false);
    setMinimizado(false);
  };

  // ── digitando no canal ativo ──────────────────────────────────────────
  const digitandoNoCanal = canalAtivo
    ? Object.entries(digitandoUsuarios)
        .filter(
          ([key]) =>
            key.startsWith(`${canalAtivo.id}_`) &&
            !key.endsWith(`_${user?.id}`)
        )
        .map(([, val]) => val)
        .filter((v) => Date.now() - v.timestamp < 4000)
    : [];

  const canaisIA = canais.filter((c) => c.tipo === 'ia');
  const canaisFixos = canais.filter((c) => c.tipo === 'grupo' && c.is_canal_fixo);
  const canaisGruposPersonalizados = canais.filter((c) => c.tipo === 'grupo' && !c.is_canal_fixo);
  const canaisDM = canais.filter((c) => c.tipo === 'dm');

  if (!user) return null;

  // ── render ────────────────────────────────────────────────────────────
  return (
    <>
      {/* ===== TOASTS DE NOVA MENSAGEM ===== */}
      <div className={styles.toastContainer}>
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={styles.toast}
            onClick={() => {
              descartarToast(toast.id);
              // Tentar abrir canal da mensagem
              const canal = canais.find((c) => c.id === toast.msg.canal_id);
              if (canal) {
                selecionarCanal(canal);
                if (!aberto) toggleAberto();
              }
            }}
          >
            <div
              className={styles.toastAvatar}
              style={{ background: getAvatarCor(toast.msg.usuario_id) }}
            >
              {getIniciais(toast.msg.autor_nome)}
            </div>
            <div className={styles.toastBody}>
              <div className={styles.toastAutor}>{toast.msg.autor_nome}</div>
              <div className={styles.toastPreview}>
                {toast.msg.imagem && !toast.msg.conteudo
                  ? '📷 Enviou uma imagem'
                  : (toast.msg.conteudo || '').substring(0, 80)}
              </div>
              <div className={styles.toastCanal}>
                {toast.msg.canal_nome
                  ? `# ${toast.msg.canal_nome}`
                  : 'Mensagem Direta'}
              </div>
            </div>
            <button
              className={styles.toastFechar}
              onClick={(e) => {
                e.stopPropagation();
                descartarToast(toast.id);
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* ===== JANELA FLUTUANTE (modal overlay) ===== */}
      {aberto && (
        <div className={`${styles.janela} ${minimizado ? styles.janelaMinimizada : ''}`}>
          {/* Cabeçalho — sempre visível, clicável para minimizar/expandir */}
          <div className={styles.janelaHeader} onClick={minimizado ? () => setMinimizado(false) : undefined}>
            {aba === 'chat' && canalAtivo ? (
              <>
                <button
                  className={styles.voltarBtn}
                  onClick={(e) => { e.stopPropagation(); setAba('canais'); }}
                  title="Voltar para canais"
                >
                  ‹
                </button>
                <div
                  className={styles.headerAvatar}
                  style={{
                    background:
                      canalAtivo.tipo === 'ia'
                        ? getAvatarCor(0)
                        : canalAtivo.tipo === 'dm'
                          ? getAvatarCor(canalAtivo.dm_usuario?.id)
                          : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                  }}
                >
                  {canalAtivo.tipo === 'ia'
                    ? '🤖'
                    : canalAtivo.tipo === 'dm'
                      ? getIniciais(canalAtivo.dm_usuario?.nome)
                      : canalAtivo.is_canal_fixo ? '💬' : '👥'}
                </div>
                <div className={styles.headerInfo}>
                  <div className={styles.headerNome}>
                    {canalAtivo.tipo === 'ia'
                      ? '🤖 TATUia'
                      : canalAtivo.tipo === 'dm'
                        ? canalAtivo.dm_usuario?.nome
                        : canalAtivo.is_canal_fixo ? `#${canalAtivo.nome}` : canalAtivo.nome}
                  </div>
                  <div className={styles.headerSub}>
                    {digitandoNoCanal.length > 0
                      ? `${digitandoNoCanal[0].nome} está digitando...`
                      : minimizado ? 'Clique para expandir' : 'Chat da Equipe'}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={styles.headerIconWrapper}>
                  <span className={styles.headerIcone}>💬</span>
                </div>
                <div className={styles.headerInfo}>
                  <span className={styles.headerTitulo}>Chat da Equipe</span>
                  {minimizado && totalNaoLidas > 0 && (
                    <span className={styles.headerSubBadge}>{totalNaoLidas} não lida{totalNaoLidas > 1 ? 's' : ''}</span>
                  )}
                </div>
                {totalNaoLidas > 0 && !minimizado && (
                  <span className={styles.headerBadge}>{totalNaoLidas}</span>
                )}
              </>
            )}
            <div className={styles.headerAcoes}>
              {!minimizado && (
                <button
                  className={styles.headerAcaoBtn}
                  onClick={(e) => { e.stopPropagation(); navigate('/chat'); }}
                  title="Abrir chat completo"
                >
                  ⤢
                </button>
              )}
              <button
                className={styles.headerAcaoBtn}
                onClick={(e) => { e.stopPropagation(); setMinimizado((v) => !v); }}
                title={minimizado ? 'Expandir' : 'Minimizar'}
              >
                {minimizado ? '▲' : '▼'}
              </button>
              <button
                className={styles.headerAcaoBtn}
                onClick={(e) => { e.stopPropagation(); fechar(); }}
                title="Fechar"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Corpo — oculto quando minimizado */}
          {!minimizado && (
            <>
              {/* ===== ABA: LISTA DE CANAIS ===== */}
              {aba === 'canais' && (
                <div className={styles.canalLista}>
                  {/* Canal TATUia */}
                  {canaisIA.map((canal) => (
                    <div
                      key={canal.id}
                      className={styles.canalItem}
                      onClick={() => selecionarCanal(canal)}
                      style={{ background: 'rgba(124, 58, 237, 0.1)', borderRadius: '8px', marginBottom: '4px', border: '1px solid rgba(124, 58, 237, 0.2)' }}
                    >
                      <div className={styles.dmAvatar} style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)', fontSize: '14px' }}>
                        🤖
                        <div className={`${styles.dmStatusDot} ${styles.dotOnline}`} />
                      </div>
                      <div className={styles.canalInfo}>
                        <div className={styles.canalNome} style={{ color: '#a855f7', fontWeight: 700 }}>{canal.nome_exibicao || 'TATUia'}</div>
                        {canal.ultima_msg_conteudo && (
                          <div className={styles.canalPreview}>
                            {canal.ultima_msg_conteudo.substring(0, 28)}
                          </div>
                        )}
                      </div>
                      {canal.nao_lidas > 0 && (
                        <span className={styles.badge} style={{ background: '#7c3aed' }}>{canal.nao_lidas}</span>
                      )}
                    </div>
                  ))}

                  {canaisFixos.length > 0 && (
                    <div className={styles.canalSecao}>
                      <div className={styles.canalSecaoTitulo}>Canais</div>
                      {canaisFixos.map((canal) => (
                        <div
                          key={canal.id}
                          className={styles.canalItem}
                          onClick={() => selecionarCanal(canal)}
                        >
                          <span className={styles.canalIcone}>
                            {canal.icone || '💬'}
                          </span>
                          <div className={styles.canalInfo}>
                            <div className={styles.canalNome}>#{canal.nome}</div>
                            {canal.ultima_msg_conteudo && (
                              <div className={styles.canalPreview}>
                                {canal.ultima_msg_autor}:{' '}
                                {canal.ultima_msg_conteudo === '[Imagem]'
                                  ? '📷'
                                  : canal.ultima_msg_conteudo.substring(0, 28)}
                              </div>
                            )}
                          </div>
                          {canal.nao_lidas > 0 && (
                            <span className={styles.badge}>{canal.nao_lidas}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Grupos Personalizados */}
                  {canaisGruposPersonalizados.length > 0 && (
                    <div className={styles.canalSecao}>
                      <div className={styles.canalSecaoTitulo}>Grupos</div>
                      {canaisGruposPersonalizados.map((canal) => (
                        <div
                          key={canal.id}
                          className={styles.canalItem}
                          onClick={() => selecionarCanal(canal)}
                        >
                          <span className={styles.canalIcone}>👥</span>
                          <div className={styles.canalInfo}>
                            <div className={styles.canalNome}>{canal.nome}</div>
                            {canal.ultima_msg_conteudo && (
                              <div className={styles.canalPreview}>
                                {canal.ultima_msg_autor}:{' '}
                                {canal.ultima_msg_conteudo === '[Imagem]'
                                  ? '📷'
                                  : canal.ultima_msg_conteudo.substring(0, 28)}
                              </div>
                            )}
                          </div>
                          {canal.nao_lidas > 0 && (
                            <span className={styles.badge}>{canal.nao_lidas}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {canaisDM.length > 0 && (
                    <div className={styles.canalSecao}>
                      <div className={styles.canalSecaoTitulo}>Mensagens Diretas</div>
                      {canaisDM.map((canal) => {
                        const outro = canal.dm_usuario;
                        const status = outro?.status_presenca || 'offline';
                        return (
                          <div
                            key={canal.id}
                            className={styles.canalItem}
                            onClick={() => selecionarCanal(canal)}
                          >
                            <div
                              className={styles.dmAvatar}
                              style={{ background: getAvatarCor(outro?.id) }}
                            >
                              {getIniciais(outro?.nome)}
                              <div
                                className={`${styles.dmStatusDot} ${styles[`dot${status.charAt(0).toUpperCase() + status.slice(1)}`]}`}
                              />
                            </div>
                            <div className={styles.canalInfo}>
                              <div className={styles.canalNome}>{outro?.nome}</div>
                              {canal.ultima_msg_conteudo && (
                                <div className={styles.canalPreview}>
                                  {canal.ultima_msg_conteudo === '[Imagem]'
                                    ? '📷 Imagem'
                                    : canal.ultima_msg_conteudo.substring(0, 28)}
                                </div>
                              )}
                            </div>
                            {canal.nao_lidas > 0 && (
                              <span className={styles.badge}>{canal.nao_lidas}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {canais.length === 0 && (
                    <div className={styles.semCanais}>Nenhum canal disponível</div>
                  )}
                </div>
              )}

              {/* ===== ABA: MINI-CHAT ===== */}
              {aba === 'chat' && canalAtivo && (
                <>
                  <div className={styles.mensagensArea} ref={mensagensRef}>
                    {carregando && (
                      <div className={styles.carregando}>
                        <div className={styles.spinner} />
                      </div>
                    )}
                    {mensagens.map((msg) => {
                      const isMeu = msg.usuario_id === user?.id;
                      return (
                        <div
                          key={msg.id}
                          className={`${styles.msgWrapper} ${isMeu ? styles.msgMeu : styles.msgOutro}`}
                        >
                          {!isMeu && (
                            <div
                              className={styles.msgAvatar}
                              style={{ background: getAvatarCor(msg.usuario_id) }}
                            >
                              {getIniciais(msg.autor_nome)}
                            </div>
                          )}
                          <div
                            className={`${styles.msgBalao} ${isMeu ? styles.msgBalaoMeu : styles.msgBalaoOutro}`}
                          >
                            {!isMeu && (
                              <div className={styles.msgAutor}>{msg.autor_nome}</div>
                            )}
                            {msg.imagem && !msg.deletada && (
                              <img
                                src={msg.imagem}
                                alt="Imagem"
                                className={styles.msgImagem}
                              />
                            )}
                            {msg.conteudo && msg.conteudo !== '[Imagem]' && (
                              <div
                                className={`${styles.msgTexto} ${msg.deletada ? styles.msgDeletada : ''}`}
                              >
                                {msg.conteudo}
                              </div>
                            )}
                            <div className={styles.msgHora}>
                              {formatarHora(msg.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {mensagens.length === 0 && !carregando && (
                      <div className={styles.semMensagens}>
                        Nenhuma mensagem ainda. Seja o primeiro! 👋
                      </div>
                    )}
                  </div>

                  {/* Pré-visualização das imagens coladas/anexadas */}
                  {imagensPendentes.length > 0 && (
                    <div className={styles.imagemPreviewArea}>
                      {imagensPendentes.map((img, idx) => (
                        <div key={idx} className={styles.imagemPreviewItem}>
                          <img src={img} alt={`Pré-visualização ${idx + 1}`} className={styles.imagemPreview} />
                          <button
                            className={styles.imagemPreviewRemover}
                            onClick={() => removerImagemPendente(idx)}
                            title="Remover imagem"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input de resposta rápida */}
                  <div className={styles.inputArea}>
                    <textarea
                      ref={inputRef}
                      className={styles.inputCampo}
                      placeholder={canalAtivo.tipo === 'ia' ? 'Pergunte ao Tatu IA...' : `Mensagem para ${canalAtivo.tipo === 'dm' ? canalAtivo.dm_usuario?.nome : '#' + canalAtivo.nome}... (cole um print com Ctrl+V)`}
                      value={inputMsg}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onPaste={handlePaste}
                      rows={1}
                    />
                    <button
                      className={styles.enviarBtn}
                      onClick={handleEnviar}
                      disabled={!inputMsg.trim() && imagensPendentes.length === 0}
                      title="Enviar (Enter)"
                    >
                      ➤
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* ===== BOTÃO FAB ===== */}
      <button
        id="chat-flutuante-btn"
        className={`${styles.fab} ${aberto && !minimizado ? styles.fabAberto : ''}`}
        onClick={toggleAberto}
        title="Chat da Equipe"
      >
        <span className={styles.fabIcone}>
          {aberto && !minimizado ? '✕' : '💬'}
        </span>
        {(!aberto || minimizado) && totalNaoLidas > 0 && (
          <span className={styles.fabBadge}>
            {totalNaoLidas > 99 ? '99+' : totalNaoLidas}
          </span>
        )}
      </button>
    </>
  );
};

export default ChatFlutuante;
